// ─────────────────────────────────────────────────────────────────────────────
// XBoatDriver — Web Bluetooth driver scaffold for the XBoat XPod oarlock.
//
// Status (2026-06): XBoat developer documentation is pending. This module is
// the architecturally complete stub — every method exists with the right
// shape and lifecycle, so the moment the GATT spec lands we only need to:
//   • Fill the UUID / handle constants below
//   • Implement `parseXPodFrame()` (separate file)
//   • Remove the TODO comments
//
// Mirrors NKEmpowerDriver's discovery + status API so the UI can share the
// `useOarlockBleConnection` hook without changes.
//
// SCULLING (TWO OARLOCKS): The driver maintains two independent connection
// SLOTS — `port` and `starboard`. Each slot tracks its own BluetoothDevice +
// GATT server and emits `crewz-gate-metrics` + `crewz-oarlock-debug-frame`
// events tagged with the matching `side`. The active app pairs slot 0 with
// the bow oarlock, slot 1 with the stroke oarlock; force balance derived
// downstream as `portAvgForceN / stbdAvgForceN * 100`.
// ─────────────────────────────────────────────────────────────────────────────

import { parseXPodFrame } from './parseXPodFrame';

// ── Placeholder GATT (filled when XBoat publishes their spec) ────────────────
// TODO: confirm with XBoat docs
const XPOD_SERVICES = {
  config: null,            // TODO: config / unlock service UUID
  data:   null,            // TODO: data-stream service UUID
  battery: 'battery_service',
  deviceInfo: 'device_information',
};

// TODO: confirm with XBoat docs
const XPOD_CHARS = {
  configWrite:  null,      // TODO: write characteristic (unlock + keepalive)
  dataStream:   null,      // TODO: per-stroke notification characteristic
  configIndicate: null,    // TODO: configuration indicate characteristic
};

const COMMON_OPTIONAL_SERVICES = [
  'battery_service',
  'device_information',
  'generic_access',
  'generic_attribute',
];

// Discovery name filter — Mode 5 auto-activates when any device whose
// advertised name starts with "XPod" is paired by the user.
const DISCOVERY_FILTERS = [
  { namePrefix: 'XPod' },
  { namePrefix: 'XPOD' },
];

// Two-slot connection map. Order matters — used by getStatus() so callers
// can render port / starboard pills consistently.
const SLOT_IDS = ['port', 'starboard'];

export default class XBoatDriver {
  constructor({ rigBySide = {} } = {}) {
    this.vendor = 'xboat';
    this.rigBySide = rigBySide;
    this.listeners = new Set();
    this._statusListeners = new Set();
    this.lastTree = null;

    // ── Per-side connection slots ─────────────────────────────────────────
    // Each slot owns its own BluetoothDevice, GATT server, notify cleanups,
    // and keepalive timer. Cleanups isolated so disconnecting one side
    // doesn't tear down the other.
    this._slots = {
      port:      this._makeSlot('port'),
      starboard: this._makeSlot('starboard'),
    };
  }

  _makeSlot(side) {
    return {
      side,
      device: null,
      server: null,
      connected: false,
      keepalive: null,
      notifyCleanups: [],
      strokeCounter: 0,
    };
  }

  static isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.bluetooth;
  }

  /** True iff EITHER slot is currently connected. Used by useAppMode → Mode 5. */
  get connected() {
    return this._slots.port.connected || this._slots.starboard.connected;
  }

  /**
   * Pair a new XPod and bind it to a side slot.
   *
   * @param {object} opts
   * @param {'port'|'starboard'} [opts.side='port']
   * @param {boolean} [opts.acceptAllDevices=false]
   */
  async requestAndConnect({ side = 'port', acceptAllDevices = false } = {}) {
    if (!XBoatDriver.isSupported()) {
      throw new Error('Web Bluetooth not supported. Use Chrome / Edge on desktop or Android.');
    }
    if (!SLOT_IDS.includes(side)) {
      throw new Error(`Invalid side '${side}'. Must be 'port' or 'starboard'.`);
    }
    const slot = this._slots[side];

    // If something is already in this slot, tear it down first so we don't
    // leak a GATT connection.
    if (slot.connected) {
      await this._disconnectSlot(side);
    }

    const requestOptions = acceptAllDevices
      ? { acceptAllDevices: true, optionalServices: COMMON_OPTIONAL_SERVICES }
      : { filters: DISCOVERY_FILTERS, optionalServices: COMMON_OPTIONAL_SERVICES };

    const device = await navigator.bluetooth.requestDevice(requestOptions);
    slot.device = device;
    device.addEventListener('gattserverdisconnected', () => this._onSlotDisconnected(side));

    const server = await device.gatt.connect();
    slot.server = server;
    slot.connected = true;
    this._emitStatus();
    return { name: device.name, id: device.id, side, connected: true };
  }

  /**
   * Run the XPod unlock + notify-enable sequence for a single slot.
   * TODO: enable notifications on the documented characteristics.
   * TODO: unlock sequence (write byte pattern XBoat docs will provide).
   *
   * @param {object} opts
   * @param {'port'|'starboard'} opts.side
   */
  async startStream({ side = 'port' } = {}) {
    const slot = this._slots[side];
    if (!slot?.server || !slot.connected) {
      throw new Error(`XPod ${side} slot not connected.`);
    }

    // TODO: enable notifications
    //   const dataSvc = await slot.server.getPrimaryService(XPOD_SERVICES.data);
    //   const dataCh  = await dataSvc.getCharacteristic(XPOD_CHARS.dataStream);
    //   await dataCh.startNotifications();
    //   dataCh.addEventListener('characteristicvaluechanged', () => this._onFrame(side, dataCh.value));

    // TODO: unlock sequence
    //   const cfgSvc = await slot.server.getPrimaryService(XPOD_SERVICES.config);
    //   const cfgCh  = await cfgSvc.getCharacteristic(XPOD_CHARS.configWrite);
    //   await cfgCh.writeValueWithResponse(new Uint8Array([/* TBD unlock bytes */]));

    // TODO: confirm keepalive requirement
    //   slot.keepalive = setInterval(async () => {
    //     try { await cfgCh.writeValueWithoutResponse(new Uint8Array([0x01])); } catch (_e) {}
    //   }, 750);

    this._emitStatus();
    return { ok: true, side, note: 'Stub — awaiting XBoat GATT spec.' };
  }

  /**
   * Frame ingest path. Called by the per-side characteristicvaluechanged
   * handler once `startStream()` is fully wired. Parses the frame, then
   * dispatches the standard CrewZ events tagged with `side`.
   *
   * Today the parser is a stub that returns null, so this function safely
   * no-ops without crashing the app.
   *
   * @param {'port'|'starboard'} side
   * @param {DataView} dv
   */
  _onFrame(side, dv) {
    try {
      const parsed = parseXPodFrame(dv, side);

      // Debug breadcrumb — fires for EVERY decoded frame (valid OR invalid)
      // so the Oarlock Debug Panel works even before parseXPodFrame() returns
      // real data. Uses the generic `crewz-oarlock-debug-frame` event so the
      // panel can be vendor-agnostic.
      try {
        const rawBytes = dv ? new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength) : new Uint8Array();
        const rawHex = Array.from(rawBytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
        window.dispatchEvent(new CustomEvent('crewz-oarlock-debug-frame', {
          detail: {
            vendor: 'xboat',
            side,
            parsed,
            rawHex,
            rawBytes: Array.from(rawBytes),
            t: Date.now(),
          },
        }));
      } catch (_e) { /* noop */ }

      if (!parsed || !parsed.isValid) return;

      const slot = this._slots[side];
      slot.strokeCounter += 1;
      const ts = Date.now();

      try {
        window.dispatchEvent(new CustomEvent('crewz-gate-metrics', {
          detail: {
            side,
            force_n: parsed.peakForceN,
            work_j: parsed.workPerStrokeJ,
            power_w: parsed.powerW,
            angle_deg: parsed.catchAngleDeg,
            stroke_count: slot.strokeCounter,
            timestamp: ts,
            avg_force_n: parsed.avgForceN,
            finish_angle_deg: parsed.finishAngleDeg,
            effective_length_deg: parsed.effectiveLengthDeg,
            drive_length_cm: parsed.driveLengthCm,
            spm: parsed.spm,
            source: 'xboat_xpod',
          },
        }));
      } catch (_e) { /* noop */ }

      if (parsed.driveTimeMs > 0 && parsed.recoveryTimeMs > 0) {
        const ratio = parsed.recoveryTimeMs / parsed.driveTimeMs;
        if (ratio >= 0.5 && ratio <= 10) {
          try {
            window.dispatchEvent(new CustomEvent('crewz-rhythm-update', {
              detail: { ratio, driveMs: parsed.driveTimeMs, recoveryMs: parsed.recoveryTimeMs, source: 'xboat_xpod', t: ts },
            }));
          } catch (_e) { /* noop */ }
        }
      }
    } catch (_e) { /* swallow per-frame */ }
  }

  /** GATT-tree introspection for the named side (used by debug surface). */
  async discoverGattTree({ side = 'port' } = {}) {
    const slot = this._slots[side];
    if (!slot?.server || !slot.connected) throw new Error(`XPod ${side} slot not connected.`);
    const services = await slot.server.getPrimaryServices();
    const tree = [];
    for (const svc of services) {
      const node = { uuid: svc.uuid, isPrimary: svc.isPrimary, characteristics: [] };
      try {
        const chars = await svc.getCharacteristics();
        for (const ch of chars) {
          node.characteristics.push({
            uuid: ch.uuid,
            properties: {
              broadcast: ch.properties.broadcast,
              read: ch.properties.read,
              writeWithoutResponse: ch.properties.writeWithoutResponse,
              write: ch.properties.write,
              notify: ch.properties.notify,
              indicate: ch.properties.indicate,
              authenticatedSignedWrites: ch.properties.authenticatedSignedWrites,
              reliableWrite: ch.properties.reliableWrite,
              writableAuxiliaries: ch.properties.writableAuxiliaries,
            },
          });
        }
      } catch (e) {
        node.characteristicsError = e?.message || String(e);
      }
      tree.push(node);
    }
    this.lastTree = { capturedAt: Date.now(), side, services: tree };
    return this.lastTree;
  }

  /** Per-slot teardown — stops keepalive, notifications, disconnects GATT. */
  async _disconnectSlot(side) {
    const slot = this._slots[side];
    if (!slot) return;
    if (slot.keepalive) {
      clearInterval(slot.keepalive);
      slot.keepalive = null;
    }
    slot.notifyCleanups.forEach(fn => { try { fn(); } catch (_e) { /* noop */ } });
    slot.notifyCleanups = [];
    if (slot.device?.gatt?.connected) {
      try { slot.device.gatt.disconnect(); } catch (_e) { /* noop */ }
    }
    slot.connected = false;
    slot.server = null;
    // device kept for re-bonding without re-prompt
    this._emitStatus();
  }

  /** Disconnect a specific side, or both when called with no args. */
  async disconnect(side) {
    if (side) {
      await this._disconnectSlot(side);
    } else {
      await this._disconnectSlot('port');
      await this._disconnectSlot('starboard');
    }
    return true;
  }

  _onSlotDisconnected(side) {
    const slot = this._slots[side];
    if (!slot) return;
    if (slot.keepalive) { clearInterval(slot.keepalive); slot.keepalive = null; }
    slot.notifyCleanups.forEach(fn => { try { fn(); } catch (_e) { /* noop */ } });
    slot.notifyCleanups = [];
    slot.connected = false;
    slot.server = null;
    this._emitStatus();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeStatus(listener) {
    this._statusListeners.add(listener);
    try { listener(this.getStatus()); } catch (_e) { /* noop */ }
    return () => this._statusListeners.delete(listener);
  }

  _emitStatus() {
    const status = this.getStatus();
    this._statusListeners.forEach(l => { try { l(status); } catch (_e) { /* noop */ } });
  }

  getStatus() {
    return {
      connected: this.connected,
      vendor: this.vendor,
      // Aggregate header info — useful for collapsed device card. The first
      // connected slot wins for `deviceName` so the badge stays simple.
      deviceName: this._slots.port.device?.name || this._slots.starboard.device?.name || null,
      deviceId: this._slots.port.device?.id || this._slots.starboard.device?.id || null,
      // Per-side detail — UI uses this to render two pills (port + starboard).
      slots: {
        port: {
          connected: this._slots.port.connected,
          deviceName: this._slots.port.device?.name || null,
          strokeCount: this._slots.port.strokeCounter,
        },
        starboard: {
          connected: this._slots.starboard.connected,
          deviceName: this._slots.starboard.device?.name || null,
          strokeCount: this._slots.starboard.strokeCounter,
        },
      },
      stream: {
        configured: !!(XPOD_SERVICES.data && XPOD_CHARS.dataStream),
        parser: 'parseXPodFrame@stub',
        // TODO: surface real UUIDs once GATT spec lands
      },
      lastTree: this.lastTree,
    };
  }

  setRigBySide(rigBySide) {
    this.rigBySide = rigBySide || {};
  }
}
